// APPROVAL ANALYTICS REPORT
// Approval workflow efficiency metrics and analysis.
// Phase 7C: Advanced Analytics

import React, {useState, useEffect} from 'react'
import {Row, Col, Card, Table, Badge, Spinner, ProgressBar} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {getApprovalAnalyticsReport} from '../../../../api/ReportsAPI'
import ExportButton from './ExportButton'

interface ApprovalAnalyticsProps {
  managerId: string
  startDate: string
  endDate: string
}

interface ApprovalSummary {
  totalSubmitted: number
  totalApproved: number
  totalRejected: number
  approvalRate: number
  averageApprovalTimeHours: number
}

interface EmployeeApproval {
  userId: string
  name: string
  submitted: number
  approved: number
  rejected: number
  approvalRate: number
}

interface RejectionReason {
  reason: string
  count: number
}

interface ApprovalData {
  summary: ApprovalSummary
  byEmployee: EmployeeApproval[]
  rejectionReasons: RejectionReason[]
}

const ApprovalAnalytics: React.FC<ApprovalAnalyticsProps> = ({
  managerId,
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ApprovalData | null>(null)

  useEffect(() => {
    if (managerId && startDate && endDate) {
      fetchReport()
    }
  }, [managerId, startDate, endDate])

  const fetchReport = async () => {
    setLoading(true)
    try {
      const response = await getApprovalAnalyticsReport({
        managerId,
        startDate,
        endDate,
      })

      if (response.data?.statusCode === 200) {
        setData(response.data.data)
      } else {
        toast.error('Failed to load approval analytics')
      }
    } catch (error: any) {
      console.error('Error fetching approval analytics:', error)
      toast.error(error?.response?.data?.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }

  // Utility function to get approval rate color
  const getApprovalRateColor = (rate: number): string => {
    if (rate >= 95) return 'success'
    if (rate >= 85) return 'info'
    if (rate >= 75) return 'warning'
    return 'danger'
  }

  // Render loading state
  if (loading) {
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
        <p className='text-muted mt-3'>Loading approval analytics...</p>
      </div>
    )
  }

  // Render empty state
  if (!data) {
    return (
      <div className='text-center py-5'>
        <i className='fas fa-check-circle fa-3x text-muted mb-3'></i>
        <p className='text-muted'>No approval data available for selected period</p>
      </div>
    )
  }

  return (
    <>
      {/* Export Button */}
      <Row className='mb-3'>
        <Col className='d-flex justify-content-end'>
          <ExportButton
            reportType='approval-analytics'
            filters={{
              managerId,
              startDate,
              endDate,
            }}
          />
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className='mb-4'>
        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-primary border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Total Submitted</p>
                  <h3 className='mb-0'>{data.summary.totalSubmitted}</h3>
                </div>
                <i className='fas fa-file-upload fa-2x text-primary opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-success border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Total Approved</p>
                  <h3 className='mb-0 text-success'>{data.summary.totalApproved}</h3>
                </div>
                <i className='fas fa-check-circle fa-2x text-success opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-danger border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Total Rejected</p>
                  <h3 className='mb-0 text-danger'>{data.summary.totalRejected}</h3>
                </div>
                <i className='fas fa-times-circle fa-2x text-danger opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-info border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Approval Rate</p>
                  <h3 className='mb-0'>
                    <Badge bg={getApprovalRateColor(data.summary.approvalRate)}>
                      {data.summary.approvalRate.toFixed(1)}%
                    </Badge>
                  </h3>
                </div>
                <i className='fas fa-percentage fa-2x text-info opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Approval Funnel Visualization */}
      <Row className='mb-4'>
        <Col>
          <Card className='shadow-sm'>
            <Card.Header>
              <h5 className='mb-0'>
                <i className='fas fa-filter me-2'></i>
                Approval Workflow Funnel
              </h5>
            </Card.Header>
            <Card.Body>
              <div className='text-center mb-3'>
                <Row>
                  <Col md={4}>
                    <div className='p-3 bg-primary bg-opacity-10 rounded'>
                      <h4 className='text-primary mb-0'>{data.summary.totalSubmitted}</h4>
                      <small className='text-muted'>Submitted</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className='p-3 bg-success bg-opacity-10 rounded'>
                      <h4 className='text-success mb-0'>{data.summary.totalApproved}</h4>
                      <small className='text-muted'>Approved</small>
                      <div className='mt-2'>
                        <small className='text-success'>
                          ↑ {((data.summary.totalApproved / data.summary.totalSubmitted) * 100).toFixed(1)}%
                        </small>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className='p-3 bg-danger bg-opacity-10 rounded'>
                      <h4 className='text-danger mb-0'>{data.summary.totalRejected}</h4>
                      <small className='text-muted'>Rejected</small>
                      <div className='mt-2'>
                        <small className='text-danger'>
                          ↓ {((data.summary.totalRejected / data.summary.totalSubmitted) * 100).toFixed(1)}%
                        </small>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Progress Bar Visualization */}
              <div className='mt-4'>
                <ProgressBar>
                  <ProgressBar
                    variant='success'
                    now={(data.summary.totalApproved / data.summary.totalSubmitted) * 100}
                    key={1}
                    label={`${data.summary.totalApproved} Approved`}
                  />
                  <ProgressBar
                    variant='danger'
                    now={(data.summary.totalRejected / data.summary.totalSubmitted) * 100}
                    key={2}
                    label={`${data.summary.totalRejected} Rejected`}
                  />
                </ProgressBar>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Employee-wise Approval Rates */}
      <Row className='mb-4'>
        <Col lg={8}>
          <Card className='shadow-sm'>
            <Card.Header>
              <h5 className='mb-0'>
                <i className='fas fa-users me-2'></i>
                Employee-wise Approval Statistics
              </h5>
            </Card.Header>
            <Card.Body>
              {data.byEmployee.length > 0 ? (
                <div className='table-responsive'>
                  <Table hover className='mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th>Employee</th>
                        <th className='text-center'>Submitted</th>
                        <th className='text-center'>Approved</th>
                        <th className='text-center'>Rejected</th>
                        <th className='text-center'>Approval Rate</th>
                        <th>Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byEmployee.map((emp, index) => (
                        <tr key={index}>
                          <td>
                            <strong>{emp.name}</strong>
                          </td>
                          <td className='text-center'>
                            <Badge bg='primary'>{emp.submitted}</Badge>
                          </td>
                          <td className='text-center'>
                            <Badge bg='success'>{emp.approved}</Badge>
                          </td>
                          <td className='text-center'>
                            <Badge bg='danger'>{emp.rejected}</Badge>
                          </td>
                          <td className='text-center'>
                            <Badge bg={getApprovalRateColor(emp.approvalRate)}>
                              {emp.approvalRate.toFixed(1)}%
                            </Badge>
                          </td>
                          <td style={{minWidth: '150px'}}>
                            <ProgressBar
                              variant={getApprovalRateColor(emp.approvalRate)}
                              now={emp.approvalRate}
                              label={`${emp.approvalRate.toFixed(0)}%`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className='text-muted text-center py-3'>No employee data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Top Rejection Reasons */}
        <Col lg={4}>
          <Card className='shadow-sm h-100'>
            <Card.Header>
              <h5 className='mb-0'>
                <i className='fas fa-exclamation-triangle me-2'></i>
                Top Rejection Reasons
              </h5>
            </Card.Header>
            <Card.Body>
              {data.rejectionReasons.length > 0 ? (
                <div className='list-group list-group-flush'>
                  {data.rejectionReasons.slice(0, 10).map((reason, index) => (
                    <div key={index} className='list-group-item px-0 py-2 border-0'>
                      <div className='d-flex justify-content-between align-items-start'>
                        <div className='flex-grow-1 me-2'>
                          <small className='text-truncate d-block' title={reason.reason}>
                            {reason.reason || 'No reason provided'}
                          </small>
                        </div>
                        <Badge bg='danger' pill>
                          {reason.count}
                        </Badge>
                      </div>
                      <div className='progress mt-1' style={{height: '4px'}}>
                        <div
                          className='progress-bar bg-danger'
                          role='progressbar'
                          style={{
                            width: `${(reason.count / data.summary.totalRejected) * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-muted text-center py-3'>No rejections in this period</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Key Insights */}
      <Row>
        <Col>
          <Card className='shadow-sm border-start border-info border-4'>
            <Card.Body>
              <h5 className='mb-3'>
                <i className='fas fa-lightbulb me-2 text-warning'></i>
                Key Insights
              </h5>
              <Row>
                <Col md={4}>
                  <div className='mb-2'>
                    <strong>Approval Efficiency:</strong>
                    <p className='mb-0 text-muted'>
                      {data.summary.approvalRate >= 95 ? (
                        <span className='text-success'>
                          ✓ Excellent! Team maintains high quality submissions.
                        </span>
                      ) : data.summary.approvalRate >= 85 ? (
                        <span className='text-info'>
                          Good. Minor improvements needed.
                        </span>
                      ) : (
                        <span className='text-warning'>
                          Needs attention. Review common rejection reasons.
                        </span>
                      )}
                    </p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className='mb-2'>
                    <strong>Team Performance:</strong>
                    <p className='mb-0 text-muted'>
                      {data.byEmployee.filter((e) => e.approvalRate < 85).length === 0 ? (
                        <span className='text-success'>✓ All employees performing well.</span>
                      ) : (
                        <span className='text-warning'>
                          {data.byEmployee.filter((e) => e.approvalRate < 85).length} employee(s)
                          need support.
                        </span>
                      )}
                    </p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className='mb-2'>
                    <strong>Common Issues:</strong>
                    <p className='mb-0 text-muted'>
                      {data.rejectionReasons.length > 0 ? (
                        <span className='text-info'>
                          Top issue: {data.rejectionReasons[0].reason.substring(0, 30)}...
                        </span>
                      ) : (
                        <span className='text-success'>✓ No rejections recorded.</span>
                      )}
                    </p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ApprovalAnalytics

